import React from 'react';
import type { INewsItem } from '../../../interfaces/News.interface';
import NoResultsFound from '../../NoResultsFound';
import BFLLink from '@repo/shared-ui/BFLLink';
import TrendingNewsClient, { TrendingNewsError } from './TrendingNewsClient';

/**
 * Base TrendingNews Component (Decoupled)
 * 
 * This is a purely presentational component that accepts all configuration via props.
 * It has no knowledge of AEM, API endpoints, or page-specific context.
 * 
 * @param trendingNews - Array of news items to display
 * @param title - Section heading title
 * @param subtitle - Section subtitle/description
 * @param maxItems - Maximum number of items to display (optional, defaults to all)
 * @param showViewAll - Whether to show "View All" link
 * @param viewAllText - Text for "View All" link
 * @param viewAllLink - URL for "View All" link
 * @param hasError - Whether there's an error state
 * @param onRetry - Callback for retry action
 * @param errorTitle - Error title text
 * @param errorDescription - Error description text
 * @param errorRetryButtonText - Retry button text
 * @param errorImageAlt - Alt text for error image
 * @param errorImageSrc - Error image source
 * @param emptyStateTitle - Empty state title
 * @param emptyStateDescription - Empty state description
 * @param emptyStateImage - Empty state image
 * @param scrollLeftAriaLabel - Aria label for left scroll button
 * @param scrollRightAriaLabel - Aria label for right scroll button
 * @param timestampLabels - Labels for timestamp formatting
 * @param images - AEM images configuration
 */
export interface TrendingNewsProps {
  trendingNews: INewsItem[];
  title?: string;
  subtitle?: string;
  maxItems?: number;
  showViewAll?: boolean;
  viewAllText?: string;
  viewAllLink?: string;
  hasError?: boolean;
  onRetry?: () => void;
  errorTitle?: string;
  errorDescription?: string;
  errorRetryButtonText?: string;
  errorImageAlt?: string;
  errorImageSrc?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateImage?: string;
  scrollLeftAriaLabel?: string;
  scrollRightAriaLabel?: string;
  timestampLabels?: {
    justNow?: string;
    minutesAgo?: string;
    hoursAgo?: string;
    daysAgo?: string;
  };
  images?: {
    defaultSourceLogo?: string;
    defaultNewsBanner?: string;
    errorImage?: string;
    noResultsImage?: string;
  };
}

/**
 * Server Component for TrendingNews
 * Handles static rendering and delegates interactive scroll functionality to client component
 */
const TrendingNews: React.FC<TrendingNewsProps> = ({
  trendingNews,
  title = "Trending",
  subtitle,
  maxItems,
  showViewAll = false,
  viewAllText = "View all",
  viewAllLink = "",
  hasError = false,
  onRetry,
  errorTitle = "Unable to fetch the information at this time",
  errorDescription = "Please try again later",
  errorRetryButtonText = "Retry",
  errorImageAlt = "Error",
  errorImageSrc = "",
  emptyStateTitle = "No results found",
  emptyStateDescription = "No trending news available at the moment.",
  emptyStateImage,
  scrollLeftAriaLabel = "Scroll trending news left",
  scrollRightAriaLabel = "Scroll trending news right",
  timestampLabels,
  images,
}) => {
  // Limit items if maxItems is specified
  const displayNews = maxItems ? trendingNews.slice(0, maxItems) : trendingNews;

  // Render section header component
  const renderHeader = () => (
    <div className="mb-[12px]">
      <h2 className="font-rubik font-medium text-[18px] md:text-[20px] leading-[100%] tracking-[0px] text-[#1A1A1A] mb-[4px] md:mb-[8px]">
        {title}
      </h2>
      <p className="text-[12px] leading-[100%] md:text-[14px] md:leading-[20px] font-rubik font-normal tracking-normal align-middle text-gray-600">
        {subtitle}
      </p>
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (hasError) {
      return (
        <TrendingNewsError
          errorTitle={errorTitle}
          errorDescription={errorDescription}
          errorRetryButtonText={errorRetryButtonText}
          errorImageAlt={errorImageAlt}
          errorImageSrc={errorImageSrc}
          onRetry={onRetry}
        />
      );
    }

    if (displayNews.length === 0) {
      return (
        <NoResultsFound
          title={emptyStateTitle}
          description={emptyStateDescription}
          aemImages={images ? { noResultsImage: emptyStateImage } : undefined}
        />
      );
    }

    return (
      <TrendingNewsClient
        displayNews={displayNews}
        scrollLeftAriaLabel={scrollLeftAriaLabel}
        scrollRightAriaLabel={scrollRightAriaLabel}
        timestampLabels={timestampLabels}
        images={images}
      />
    );
  };

  return (
    <div className="px-4 py-3">
      {renderHeader()}
      {renderContent()}
      {showViewAll && viewAllLink && (
        <BFLLink openInNewTab href={viewAllLink}>
          <div className="mt-[8px] max-w-fit cursor-pointer border-0 bg-transparent px-0 pb-[4px] pt-[8px] text-left transition-opacity duration-200 ease-linear">
            <button
              type="button"
              className="text-link-primary cursor-pointer font-[500] text-[14px]"
            >
              {viewAllText}
            </button>
          </div>
        </BFLLink>
      )}
    </div>
  );
};

export default TrendingNews;

